import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateNodeData } from '../../../../store/workflow/workflowSlice';
import { resolveEndpoint, producedColumns } from '../../utils/transformLoad';
import type { Column } from '../../utils/transformLoad';
import { typeLabelOf } from '../../nodeCatalog';
import { usePersistentState } from '../../../../utils/usePersistentState';
import ExtractColumnsModal from './ExtractColumnsModal';
import { Section } from './AnalyticsNodePanel';

/**
 * 내부데이터 노드 속성 — 집계·데이터비교와 같은 모양(2026-10-20, 예전 RJSF 폼·파란 버튼 화면을 바꿨다).
 *
 * 항목은 앞 처리가 내보내는 것(producedColumns)이고, 「항목상세」 에서 고른 것(dataFields)은 **화면에서만** 쓴다 —
 * 뒤 노드(매핑·조건·키)의 항목 목록이 그것으로 좁혀진다. 실행할 때 데이터를 거르지는 않는다(서버 pass_data 는 그대로 넘긴다).
 */
interface Props {
  node: any;
  /** 앞 처리에서 항목을 못 구할 때 쓸 항목(없으면 빈 목록) */
  legacyColumns?: Column[];
  onOpenNodeData: () => void;
  nodeDataBusy: boolean;
}

const InnerDataPanel: React.FC<Props> = ({ node, legacyColumns = [], onOpenNodeData, nodeDataBusy }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const nodes = useSelector((s: any) => s.workflow.nodes);
  const edges = useSelector((s: any) => s.workflow.edges);
  const cfg = node?.data?.config || {};
  const patch = useCallback((p: Record<string, any>) => {
    dispatch(updateNodeData({ id: node.id, data: { config: { ...cfg, ...p }, ...(p.label !== undefined ? { label: p.label } : {}) } } as any));
  }, [cfg, dispatch, node.id]);
  const [modalOpen, setModalOpen] = useState(false);
  const [notesOpen, setNotesOpen] = usePersistentState('prop:innerDataNotesOpen', true);

  const upstream = useMemo(() => resolveEndpoint(node.id, 'up', nodes, edges), [node.id, nodes, edges]);
  const columns: Column[] = useMemo(() => {
    const produced = upstream ? (upstream.kind === 'inner' ? producedColumns(upstream.node.id, nodes, edges) : upstream.columns) : [];
    return produced.length ? produced : legacyColumns;
  }, [upstream, nodes, edges, legacyColumns]);
  const picked: string[] = (cfg.dataFields || []).map((x: any) => (typeof x === 'string' ? x : x?.name)).filter(Boolean);
  const shown = picked.length ? picked : columns.map((c) => c.name);

  return (
    <div className="h-full bg-white p-4 overflow-auto">
      <div className="mb-3 pb-3 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{t('property:ui.properties')}</h3>
        <div className="flex items-center gap-2"><span className="text-xs font-semibold text-gray-500">{t('property:an.nodeType')}</span>
          <input type="text" value={typeLabelOf('innerData')} readOnly className="text-xs text-gray-700 bg-gray-100 border-none p-1 rounded w-auto" style={{ minWidth: 80 }} />
          <button type="button" className="ml-auto px-2 py-0.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 disabled:opacity-50"
            disabled={nodeDataBusy} onClick={onOpenNodeData} title={t('property:legacy.nodeDataTitle')}>
            {nodeDataBusy ? t('property:legacy.opening') : t('property:legacy.nodeData')}
          </button>
        </div>
      </div>
      <div className="mb-3"><label className="block text-xs font-semibold text-gray-500 mb-1">{t('workflow:field.label')}</label>
        <input className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={cfg.label ?? node.data?.label ?? ''}
          placeholder={t('workflow:field.labelPlaceholder')} onChange={(e) => patch({ label: e.target.value })} /></div>
      <div className="mb-3 text-[0.6875rem] text-gray-500">
        {/* 앞이 처리 노드면 「내부데이터」 가 아니라 그 처리의 유형으로 보인다(예: 데이터처리 · 추출) */}
        {upstream ? t('property:an.inputFrom', { kind: upstream.kind === 'inner' ? typeLabelOf(upstream.node.type) : upstream.kindLabel, name: upstream.name, n: columns.length }) : t('property:an.noUpstream')}
      </div>

      <Section title={t('property:inner.columns', { n: shown.length })} hint={t('property:inner.columnsHint')}>
        {shown.length ? (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {shown.map((c) => <span key={c} className="px-1.5 py-0.5 rounded bg-gray-100 text-xs text-gray-700 font-mono">{c}</span>)}
          </div>
        ) : <div className="text-[0.6875rem] text-gray-400 mb-1.5">{t('property:inner.noColumns')}</div>}
        <div className="flex items-center gap-2">
          <button type="button" className="px-2 py-0.5 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
            disabled={!columns.length} onClick={() => setModalOpen(true)}>{t('property:legacy.columnDetails')}</button>
          {picked.length > 0 && (
            <button type="button" className="text-[0.6875rem] text-blue-600 hover:underline" onClick={() => patch({ dataFields: [] })}>
              {t('property:inner.useAll')}</button>
          )}
          {picked.length > 0 && <span className="text-[0.6875rem] text-gray-500">{t('property:inner.picked', { n: picked.length, total: columns.length })}</span>}
        </div>
      </Section>

      {/* 내부데이터의 특징·제약 — SAM 파일 대신 쓸지 판단할 수 있게 (2026-10-20 현장 질문) */}
      <details className="mb-3 rounded border border-amber-200 bg-amber-50 text-xs text-gray-700"
        open={notesOpen} onToggle={(e) => setNotesOpen((e.currentTarget as HTMLDetailsElement).open)}>
        <summary className="cursor-pointer select-none px-2 py-1.5 font-semibold text-amber-800">{t('property:innerNotes.title')}</summary>
        <ul className="list-disc space-y-1 pb-2 pl-6 pr-2">
          {(['i1', 'i2', 'i3', 'i4', 'i5'] as const).map((k) => <li key={k}>{t(`property:innerNotes.${k}`)}</li>)}
        </ul>
        <p className="px-2 pb-2 text-gray-600">{t('property:innerNotes.advice')}</p>
      </details>

      {modalOpen && (
        <ExtractColumnsModal
          columns={columns as any}
          selected={cfg.dataFields || []}
          onSave={(selectedColumns) => { patch({ dataFields: selectedColumns }); setModalOpen(false); }}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
};

export default InnerDataPanel;
